import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Checkbox,
  InlineNotification,
  ToastNotification,
  SkeletonText,
  OverflowMenu,
  OverflowMenuItem,
  Modal
} from '@carbon/react';
import { Add, Close, Asleep, Light, PartlyCloudy, Building, ChevronRight, Edit, Time, UserMultiple, Activity, ArrowLeft } from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';
import Groups from './Groups';
import Activities from './Activities';
import axiosInstance from '../utils/axiosInstance';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import { canAccessPath, canSeeOrganizationSettings } from '../config/routePermissions';
import './Settings.css';

// Tabovi stranice „Organizacija". „Grupe" i „Aktivnosti" su ranije bile
// zasebne stranice (svaka sa svojom stavkom u meniju), a sada su tabovi ove
// stranice. Svaki tab ima svoju rutu (`path`) - zato stari linkovi
// (/activities, /residents/groups) i dalje rade, a adresa prati izabrani tab.
const TABS = [
  { id: 'settings', label: 'Oblasti i smene', path: '/settings', icon: Time },
  { id: 'groups', label: 'Grupe', path: '/residents/groups', icon: UserMultiple },
  { id: 'activities', label: 'Aktivnosti', path: '/activities', icon: Activity },
];

const Settings = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  const navigate = useNavigate();
  const location = useLocation();
  const { canAny, permissions, isAdmin } = usePermissions();

  // Tabove za koje korisnik nema dozvolu ne prikazujemo. Tab „Oblasti i smene"
  // nema dozvolu na samoj ruti, pa se njegova vidljivost računa preko pomoćne
  // funkcije (inače bi podešavanja videli i oni kojima je stranica vidljiva
  // samo zbog taba „Grupe").
  const visibleTabs = useMemo(
    () =>
      TABS.filter((tab) =>
        tab.id === 'settings'
          ? canSeeOrganizationSettings({ canAny, permissions, isAdmin })
          : canAccessPath(tab.path, { canAny, permissions, isAdmin })
      ),
    [canAny, permissions, isAdmin]
  );

  // Aktivni tab određuje adresa (ruta), pa osvežavanje stranice i
  // back/forward rade ispravno.
  const requestedTabId =
    TABS.find((tab) => tab.path === location.pathname)?.id || 'settings';

  // Ako je traženi tab korisniku nedozvoljen, prebaci na prvi dozvoljeni
  // (npr. `/settings` bez dozvole za smene -> tab „Grupe").
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (visibleTabs.some((tab) => tab.id === requestedTabId)) return;
    navigate(visibleTabs[0].path, { replace: true });
  }, [visibleTabs, requestedTabId, navigate]);

  const activeTab = visibleTabs.some((tab) => tab.id === requestedTabId)
    ? requestedTabId
    : visibleTabs[0]?.id;

  // Kao na mobilnom profilu rezidenta: tab traka se horizontalno skroluje, a
  // aktivni tab se sam dovodi u vidno polje. Kada se ide udesno, aktivni tab se
  // poravnava sa LEVOM ivicom trake; kada se ide ulevo, sa DESNOM (suprotan
  // smer) - identično kao `.residents1-tabs__list` u ResidentProfile.js.
  const tabListRef = useRef(null);
  const prevTabIndexRef = useRef(null);

  const scrollActiveTabIntoView = useCallback(() => {
    const listEl = tabListRef.current;
    if (!listEl) return;
    const activeTabEl = listEl.querySelector('.org-tab--active');
    if (!activeTabEl) return;

    const currentIndex = visibleTabs.findIndex((tab) => tab.id === activeTab);
    const prevIndex = prevTabIndexRef.current ?? currentIndex;
    const goingRight = currentIndex >= prevIndex;

    // getBoundingClientRect je precizan i nezavisan od roditelja/skrola.
    const listRect = listEl.getBoundingClientRect();
    const tabRect = activeTabEl.getBoundingClientRect();

    const targetScrollLeft = goingRight
      ? listEl.scrollLeft + (tabRect.left - listRect.left)
      : listEl.scrollLeft + (tabRect.right - listRect.right);

    if (typeof listEl.scrollTo === 'function') {
      listEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    } else {
      listEl.scrollLeft = targetScrollLeft;
    }
    prevTabIndexRef.current = currentIndex;
  }, [activeTab, visibleTabs]);

  // Skrolujemo u sledećem frejmu: tada je layout (i `--active` klasa) sigurno
  // primenjen, pa se aktivni tab uvek dovede u vidno polje.
  useEffect(() => {
    const frame = window.requestAnimationFrame(scrollActiveTabIntoView);
    return () => window.cancelAnimationFrame(frame);
  }, [scrollActiveTabIntoView]);

  // Pri promeni širine ekrana / orijentacije vrati aktivni tab u vidno polje.
  useEffect(() => {
    const handleResize = () => scrollActiveTabIntoView();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollActiveTabIntoView]);

  // Tastaturna navigacija tabovima (strelice levo/desno, Home/End) - kao na
  // profilu rezidenta.
  const handleTabKeyDown = (event, index) => {
    const tabs = visibleTabs;
    if (tabs.length === 0) return;
    let newIndex = index;
    if (event.key === 'ArrowRight') {
      newIndex = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      newIndex = 0;
    } else if (event.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    navigate(tabs[newIndex].path);
    const tabButtons = tabListRef.current?.querySelectorAll('.org-tab');
    tabButtons?.[newIndex]?.focus();
  };

  // ===== Shift Types state =====
  const [shiftTypes, setShiftTypes] = useState({});
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    areaId: '',
    startTime: '08:00',
    endTime: '16:00',
    endsNextDay: false
  });

  // ===== Areas state =====
  const [isAreaPanelOpen, setIsAreaPanelOpen] = useState(false);
  const [areaName, setAreaName] = useState('');
  const [isAreaSaving, setIsAreaSaving] = useState(false);
  const [areaFormError, setAreaFormError] = useState(null);
  const [isAreaDeleteModalOpen, setIsAreaDeleteModalOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);

  // Master-detail: leva lista oblasti bira koju oblast (i njene tipove smena)
  // prikazujemo u desnoj kartici.
  const [selectedAreaId, setSelectedAreaId] = useState(null);

  // „Oblasti i smene" je master-detail (lista oblasti | tipovi smena). Na
  // uskom ekranu (< 1025px, gde CSS lomi raspored u jednu kolonu) obe kartice
  // ne mogu da stoje jedna pored druge, pa se ponašamo kao mobilna aplikacija:
  // prvo lista oblasti, a tap na oblast otvara „detalj" (tipove smena) uz dugme
  // „Oblasti" za povratak na listu.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 1024px)').matches
      : false
  );
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  // Prati prelazak usko <-> široko (isti obrazac kao u EmployeesTable/Meals).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(max-width: 1024px)');
    const handleChange = (event) => {
      setIsNarrow(event.matches);
      // Na širokom ekranu su obe kartice opet vidljive, pa „detalj" nije
      // poseban ekran - resetujemo stanje da ne ostane „zaključano".
      if (!event.matches) setIsMobileDetailOpen(false);
    };
    if (mql.addEventListener) mql.addEventListener('change', handleChange);
    else if (mql.addListener) mql.addListener(handleChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handleChange);
      else if (mql.removeListener) mql.removeListener(handleChange);
    };
  }, []);

  // Izbor oblasti: na uskom ekranu odmah otvaramo „detalj" (tipove smena).
  const handleSelectArea = (areaId) => {
    setSelectedAreaId(areaId);
    if (isNarrow) setIsMobileDetailOpen(true);
  };

  // Skrol kontejner stranice - pri smeni „lista <-> detalj" vraćamo skrol na
  // vrh, da korisnik ne ostane u sredini novog sadržaja (lista ume da bude
  // dugačka, pa je prethodna pozicija besmislena za drugi ekran).
  const settingsPageRef = useRef(null);

  useEffect(() => {
    if (settingsPageRef.current) settingsPageRef.current.scrollTop = 0;
  }, [isMobileDetailOpen]);

  const showToast = (kind, title, subtitle = '') => {
    setToast({ show: true, kind, title, subtitle });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
  };

  const extractData = (res) => {
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.data)) return res.data.data;
    if (res.data && Array.isArray(res.data.value)) return res.data.value;
    if (res.data && Array.isArray(res.data.$values)) return res.data.$values;
    return [];
  };

  const fetchShiftTypes = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/ShiftType');
      if (response.data.success) {
        const shiftsByArea = response.data.data.shiftTypesByArea || {};
        setShiftTypes(shiftsByArea);
      }
    } catch (error) {
      showToast('error', 'Greška pri učitavanju tipova smena');
      console.error('Error fetching shift types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axiosInstance.get('/api/Area');
      setAreas(extractData(response));
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  useEffect(() => {
    fetchShiftTypes();
    fetchAreas();
  }, []);

  // Izabrana oblast u levom spisku. Ako korisnik još nije ništa izabrao (ili
  // je izabrana oblast obrisana), prikazujemo prvu oblast iz liste - tako nema
  // dodatnog sinhronizacionog state-a.
  const selectedArea = useMemo(() => {
    const found = areas.find((area) => area.id === selectedAreaId);
    return found || areas[0] || null;
  }, [areas, selectedAreaId]);

  // Tipovi smena sa servera stižu grupisani po NAZIVU oblasti
  // (response.data.data.shiftTypesByArea).
  const selectedAreaShifts = selectedArea ? shiftTypes[selectedArea.name] || [] : [];

  // ===== Shift Types handlers =====
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      areaId: '',
      startTime: '08:00',
      endTime: '16:00',
      endsNextDay: false
    });
  };

  const handleOpenPanel = (shift = null) => {
    if (shift) {
      setCurrentShift(shift);
      setFormData({
        name: shift.name,
        description: shift.description,
        areaId: shift.areaId,
        startTime: shift.startTime?.substring(0, 5) || '08:00',
        endTime: shift.endTime?.substring(0, 5) || '16:00',
        endsNextDay: shift.endsNextDay
      });
    } else {
      setCurrentShift(null);
      resetForm();
      // Novi tip smene odmah pripada oblasti izabranoj u levom spisku.
      if (selectedArea) {
        setFormData((prev) => ({ ...prev, areaId: String(selectedArea.id) }));
      }
    }
    setFormError(null);
    setIsSidePanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsSidePanelOpen(false);
    setCurrentShift(null);
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.areaId) {
      setFormError('Molimo popunite sva obavezna polja');
      return;
    }

    const startTime = `${formData.startTime}:00`;
    const endTime = `${formData.endTime}:00`;
    const formattedEndTime = formData.endsNextDay ? `->${endTime}` : endTime;

    const payload = {
      name: formData.name,
      description: formData.description,
      areaId: formData.areaId,
      startTime: startTime,
      endTime: formattedEndTime
    };

    setIsSaving(true);
    setFormError(null);

    try {
      if (currentShift) {
        payload.id = currentShift.id;
        const response = await axiosInstance.put(`/api/ShiftType/${currentShift.id}`, payload);
        if (response.data.success) {
          showToast('success', 'Uspešno!', 'Tip smene uspešno ažuriran');
          fetchShiftTypes();
          handleClosePanel();
        }
      } else {
        const response = await axiosInstance.post('/api/ShiftType', payload);
        if (response.data.success) {
          showToast('success', 'Uspešno!', 'Tip smene uspešno kreiran');
          fetchShiftTypes();
          handleClosePanel();
        }
      }
    } catch (error) {
      setFormError(error.response?.data?.message || 'Greška pri čuvanju tipa smene');
      console.error('Error saving shift type:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentShift) return;

    try {
      const response = await axiosInstance.delete(`/api/ShiftType/${currentShift.id}`);
      if (response.data.success) {
        showToast('success', 'Uspešno!', 'Tip smene uspešno obrisan');
        fetchShiftTypes();
        setIsDeleteModalOpen(false);
        setCurrentShift(null);
      }
    } catch (error) {
      showToast('error', 'Greška!', error.response?.data?.message || 'Greška pri brisanju tipa smene');
      console.error('Error deleting shift type:', error);
    }
  };

  const handleOpenDeleteModal = (shift) => {
    setCurrentShift(shift);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCurrentShift(null);
  };

  const getShiftIcon = (shift) => {
    if (!shift || !shift.startTime) {
      return { Icon: PartlyCloudy, color: '#9fb746', label: 'Standardna' };
    }

    const startHour = parseInt(shift.startTime.split(':')[0] || '0');

    if (startHour >= 20 || startHour < 6) {
      return { Icon: Asleep, color: '#6B4EFF', label: 'Noćna' };
    }
    else if (startHour >= 6 && startHour < 12) {
      return { Icon: Light, color: '#FFA500', label: 'Jutarnja' };
    }
    else {
      return { Icon: PartlyCloudy, color: '#FF6B35', label: 'Popodnevna' };
    }
  };

  // „4 tipa smena" / „Bez tipova smena" - podnaslov oblasti u levom spisku.
  const formatShiftCountLabel = (count) => {
    if (!count) return 'Bez tipova smena';
    if (count === 1) return '1 tip smene';
    const lastTwo = count % 100;
    const last = count % 10;
    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
      return `${count} tipa smena`;
    }
    return `${count} tipova smena`;
  };

  const formatTimeRange = (shift) => {
    const startTime = shift.startTime?.substring(0, 5);
    const endTime = shift.formattedEndTime?.substring(shift.endsNextDay ? 2 : 0, shift.endsNextDay ? 7 : 5);
    return `${startTime} – ${shift.endsNextDay ? '→' : ''}${endTime}`;
  };

  // Trajanje smene se računa iz početka i kraja (npr. "8 h", "17 h 55 min").
  const getShiftDurationLabel = (shift) => {
    const toMinutes = (value) => {
      if (!value) return null;
      const [hours, minutes] = String(value).replace('->', '').split(':');
      if (hours === undefined || minutes === undefined) return null;
      return Number(hours) * 60 + Number(minutes);
    };

    const start = toMinutes(shift?.startTime);
    const end = toMinutes(shift?.formattedEndTime || shift?.endTime);
    if (start === null || end === null) return '—';

    let diff = end - start;
    if (shift.endsNextDay || diff < 0) diff += 24 * 60;
    if (diff <= 0) return '—';

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
  };

  // ===== Areas handlers =====
  const openAreaPanel = () => {
    setAreaName('');
    setAreaFormError(null);
    setIsAreaPanelOpen(true);
  };

  const closeAreaPanel = () => {
    setIsAreaPanelOpen(false);
    setAreaFormError(null);
  };

  const handleAreaSubmit = async (e) => {
    e.preventDefault();
    if (!areaName.trim()) {
      setAreaFormError('Naziv oblasti je obavezan.');
      return;
    }
    try {
      setIsAreaSaving(true);
      setAreaFormError(null);
      await axiosInstance.post('/api/Area', { name: areaName.trim() });
      closeAreaPanel();
      await fetchAreas();
      showToast('success', 'Uspešno!', `Oblast "${areaName.trim()}" je dodata.`);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Greška pri dodavanju oblasti.';
      setAreaFormError(msg);
    } finally {
      setIsAreaSaving(false);
    }
  };

  const openAreaDeleteModal = (area) => {
    setAreaToDelete(area);
    setIsAreaDeleteModalOpen(true);
  };

  const confirmAreaDelete = async () => {
    if (!areaToDelete) return;
    try {
      await axiosInstance.delete(`/api/Area/${areaToDelete.id}`);
      setAreas(prev => prev.filter(a => a.id !== areaToDelete.id));
      showToast('success', 'Uspešno!', `Oblast "${areaToDelete.name}" je obrisana.`);
    } catch (err) {
      showToast('error', 'Greška!', 'Nije moguće obrisati oblast. Pokušajte ponovo.');
    } finally {
      setIsAreaDeleteModalOpen(false);
      setAreaToDelete(null);
    }
  };

  const settingsContent = (
      <div
        ref={settingsPageRef}
        className={`settings-page ${isDarkMode ? 'settings-page--dark' : ''}`}
      >
        {/* ===== Oblasti (levo) + tipovi smena izabrane oblasti (desno) ===== */}
        <div
          className={`org-areas-layout${isNarrow ? ' org-areas-layout--narrow' : ''}${
            isNarrow && isMobileDetailOpen ? ' org-areas-layout--detail' : ''
          }`}
        >
          {/* Na uskom ekranu se lista oblasti i „detalj" (tipovi smena) smenjuju,
              pa se montira samo aktivni ekran. Na širokom ekranu su obe kartice. */}
          {!(isNarrow && isMobileDetailOpen) && (
          <section className="org-areas-card" aria-label="Oblasti">
            <div className="org-areas-card__header">
              <h2 className="org-areas-card__title">
                Oblasti
                <span className="org-areas-card__count">{areas.length}</span>
              </h2>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Add}
                onClick={openAreaPanel}
                className="org-add-area-button"
              >
                Dodaj oblast
              </Button>
            </div>

            <div className="org-areas-list">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="org-area-row org-area-row--skeleton">
                    <SkeletonText width="60%" />
                  </div>
                ))
              ) : areas.length === 0 ? (
                <p className="org-areas-empty">Nema dodanih oblasti.</p>
              ) : (
                areas.map((area) => {
                  const isActive = selectedArea ? area.id === selectedArea.id : false;
                  return (
                    <button
                      key={area.id}
                      type="button"
                      className={`org-area-row${isActive ? ' org-area-row--active' : ''}`}
                      aria-current={isActive ? 'true' : undefined}
                      onClick={() => handleSelectArea(area.id)}
                    >
                      <span className="org-area-row__icon" aria-hidden="true">
                        <Building size={20} />
                      </span>
                      <span className="org-area-row__body">
                        <span className="org-area-row__name">{area.name}</span>
                        <span className="org-area-row__meta">
                          {formatShiftCountLabel((shiftTypes[area.name] || []).length)}
                        </span>
                      </span>
                      <ChevronRight size={16} className="org-area-row__chevron" aria-hidden="true" />
                    </button>
                  );
                })
              )}
            </div>
          </section>
          )}

          {!(isNarrow && !isMobileDetailOpen) && (
          <section className="org-shifts-card" aria-label="Tipovi smena">
            {(selectedArea || isNarrow) && (
              <div className="org-shifts-card__header">
                {isNarrow ? (
                  /* Povratak sa „detalja" na listu oblasti (samo uski ekran). */
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ArrowLeft}
                    onClick={() => setIsMobileDetailOpen(false)}
                    className="org-detail-back"
                  >
                    Oblasti
                  </Button>
                ) : (
                  <span className="org-shifts-card__icon" aria-hidden="true">
                    <Building size={22} />
                  </span>
                )}
                <div className="org-shifts-card__heading">
                  <h2 className="org-shifts-card__title">
                    {selectedArea ? selectedArea.name : 'Tipovi smena'}
                  </h2>
                  <p className="org-shifts-card__subtitle">
                    {selectedArea
                      ? 'Tipovi smena za izabranu oblast.'
                      : 'Izaberite oblast sa liste.'}
                  </p>
                </div>
                {selectedArea && (
                  <div className="org-shifts-card__actions">
                    <OverflowMenu size="sm" flipped iconDescription="Opcije oblasti">
                      <OverflowMenuItem
                        itemText="Obriši oblast"
                        isDelete
                        onClick={() => openAreaDeleteModal(selectedArea)}
                      />
                    </OverflowMenu>
                    <Button
                      kind="primary"
                      size="sm"
                      renderIcon={Add}
                      onClick={() => handleOpenPanel()}
                      className="add-shift-type-button"
                    >
                      Dodaj tip smene
                    </Button>
                  </div>
                )}
              </div>
            )}

            {selectedArea ? (
              <div className="org-shifts-table">
                  <div className="org-shifts-table__head">
                    <span className="org-shifts-table__col">Naziv smene</span>
                    <span className="org-shifts-table__col">Vreme</span>
                    <span className="org-shifts-table__col">Trajanje</span>
                    <span className="org-shifts-table__col org-shifts-table__col--actions">Akcije</span>
                  </div>

                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="org-shifts-table__row org-shifts-table__row--skeleton">
                        <SkeletonText width="45%" />
                      </div>
                    ))
                  ) : selectedAreaShifts.length === 0 ? (
                    <p className="org-shifts-table__empty">
                      Nema tipova smena za ovu oblast. Dodajte prvi tip smene.
                    </p>
                  ) : (
                    selectedAreaShifts.map((shiftItem) => {
                      const icon = getShiftIcon(shiftItem);
                      return (
                        <div key={shiftItem.id} className="org-shifts-table__row">
                          <span className="org-shifts-table__name">
                            <span
                              className="org-shift-icon"
                              style={{ backgroundColor: `${icon.color}1f`, color: icon.color }}
                              aria-hidden="true"
                            >
                              {React.createElement(icon.Icon, { size: 18 })}
                            </span>
                            <span className="org-shifts-table__name-text">{shiftItem.name}</span>
                          </span>
                          <span className="org-shifts-table__time">{formatTimeRange(shiftItem)}</span>
                          <span className="org-shifts-table__duration">{getShiftDurationLabel(shiftItem)}</span>
                          <span className="org-shifts-table__actions">
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={Edit}
                              hasIconOnly
                              iconDescription="Izmeni tip smene"
                              onClick={() => handleOpenPanel(shiftItem)}
                              className="org-row-icon-button"
                            />
                            <OverflowMenu size="sm" flipped iconDescription="Opcije tipa smene">
                              <OverflowMenuItem
                                itemText="Izmeni"
                                onClick={() => handleOpenPanel(shiftItem)}
                              />
                              <OverflowMenuItem
                                itemText="Obriši"
                                hasDivider
                                isDelete
                                onClick={() => handleOpenDeleteModal(shiftItem)}
                              />
                            </OverflowMenu>
                          </span>
                        </div>
                      );
                    })
                  )}
              </div>
            ) : (
              <p className="org-shifts-card__empty">
                {areas.length === 0
                  ? 'Dodajte oblast da biste mogli da definišete tipove smena.'
                  : 'Izaberite oblast sa liste da biste videli njene tipove smena.'}
              </p>
            )}
          </section>
          )}
        </div>

        {/* ===== Shift Type Right Side Panel ===== */}
        <div className={`shift-type-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
              {currentShift ? 'Izmeni tip smene' : 'Dodaj tip smene'}
            </h3>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              iconDescription="Zatvori"
              hasIconOnly
              onClick={handleClosePanel}
            />
          </div>

          {formError && (
            <div style={{
              position: 'fixed',
              top: '4rem',
              right: '1rem',
              zIndex: 9999,
              minWidth: '320px',
              animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <style>
                {`
                  @keyframes slideInFromRight {
                    from {
                      transform: translateX(calc(100% + 1rem));
                      opacity: 0;
                    }
                    to {
                      transform: translateX(0);
                      opacity: 1;
                    }
                  }
                `}
              </style>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}

          <div className="side-panel-content">
            <form onSubmit={handleSubmit} className="shift-type-form">
              <TextInput
                id="name"
                labelText="Naziv smene *"
                placeholder="npr. Jutarnja smena"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={200}
                disabled={isSaving}
              />

              <Select
                id="areaId"
                labelText="Oblast *"
                value={formData.areaId}
                onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
                disabled={isSaving}
              >
                <SelectItem value="" text="Izaberite oblast" />
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id} text={area.name} />
                ))}
              </Select>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Početak smene *
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Select
                      id="start-hour"
                      labelText=""
                      hideLabel
                      value={formData.startTime.split(':')[0]}
                      onChange={e => setFormData({ ...formData, startTime: `${e.target.value}:${formData.startTime.split(':')[1]}` })}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="start-minute"
                      labelText=""
                      hideLabel
                      value={formData.startTime.split(':')[1]}
                      onChange={e => setFormData({ ...formData, startTime: `${formData.startTime.split(':')[0]}:${e.target.value}` })}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Kraj smene *
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Select
                      id="end-hour"
                      labelText=""
                      hideLabel
                      value={formData.endTime.split(':')[0]}
                      onChange={e => setFormData({ ...formData, endTime: `${e.target.value}:${formData.endTime.split(':')[1]}` })}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="end-minute"
                      labelText=""
                      hideLabel
                      value={formData.endTime.split(':')[1]}
                      onChange={e => setFormData({ ...formData, endTime: `${formData.endTime.split(':')[0]}:${e.target.value}` })}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              <Checkbox
                id="endsNextDay"
                labelText="Smena se završava narednog dana"
                checked={formData.endsNextDay}
                onChange={(e) => setFormData({ ...formData, endsNextDay: e.target.checked })}
                helperText="Označite za noćne smene koje traju do sledećeg jutra"
                disabled={isSaving}
              />

              <TextArea
                id="description"
                labelText="Opis"
                placeholder="Detaljan opis smene..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                disabled={isSaving}
              />

              <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={isSaving}
                  className="add-shift-type-button"
                >
                  {isSaving ? 'Čuvanje...' : currentShift ? 'Sačuvaj' : 'Kreiraj'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {isSidePanelOpen && (
          <div
            className="side-panel-overlay"
            onClick={handleClosePanel}
          />
        )}

        {/* ===== Area Right Side Panel ===== */}
        <div className={`shift-type-side-panel ${isAreaPanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
              Nova oblast
            </h3>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              iconDescription="Zatvori"
              hasIconOnly
              onClick={closeAreaPanel}
            />
          </div>

          {areaFormError && (
            <div style={{ padding: '1rem 1.5rem 0' }}>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={areaFormError}
                onCloseButtonClick={() => setAreaFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}

          <div className="side-panel-content">
            <form onSubmit={handleAreaSubmit} className="shift-type-form">
              <TextInput
                id="area-name"
                labelText="Naziv oblasti"
                placeholder="Unesite naziv"
                value={areaName}
                onChange={(e) => setAreaName(e.target.value)}
                disabled={isAreaSaving}
                required
              />

              <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                <Button
                  kind="secondary"
                  onClick={closeAreaPanel}
                  disabled={isAreaSaving}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={isAreaSaving}
                  className="add-shift-type-button"
                >
                  {isAreaSaving ? 'Dodavanje...' : 'Dodaj'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {isAreaPanelOpen && (
          <div
            className="side-panel-overlay"
            onClick={closeAreaPanel}
          />
        )}

        {/* ===== Shift Type Delete Modal ===== */}
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={handleCloseDeleteModal}
          modalHeading="Brisanje tipa smene"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={handleDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete tip smene{' '}
            <strong>{currentShift?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* ===== Area Delete Modal ===== */}
        <Modal
          open={isAreaDeleteModalOpen}
          onRequestClose={() => setIsAreaDeleteModalOpen(false)}
          modalHeading="Brisanje oblasti"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={confirmAreaDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete oblast{' '}
            <strong>{areaToDelete?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* Toast */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              lowContrast
              onClose={() => setToast(t => ({ ...t, show: false }))}
            />
          </div>
        )}
      </div>
  );

  return (
    <PageContainer>
      <div className="org-page">
        {/* Tabovi: Oblasti i smene / Grupe / Aktivnosti */}
        <nav className="org-tabs" aria-label="Sekcije organizacije">
          <div
            className="org-tabs__list"
            role="tablist"
            ref={tabListRef}
            aria-label="Sekcije organizacije"
          >
            {visibleTabs.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`org-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`org-panel-${tab.id}`}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={`org-tab${activeTab === tab.id ? ' org-tab--active' : ''}`}
                  onClick={() => navigate(tab.path)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <main
          className="org-main"
          id={`org-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`org-tab-${activeTab}`}
        >
          {activeTab === 'groups' ? (
            <Groups embedded />
          ) : activeTab === 'activities' ? (
            <Activities embedded />
          ) : activeTab === 'settings' ? (
            settingsContent
          ) : null}
        </main>
      </div>
    </PageContainer>
  );
};

export default Settings;
